import { relations, sql } from 'drizzle-orm'
import { index, integer, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'
import z from 'zod/v4'

import { organization } from '../../auth'
import { Invoice } from '../invoice'
import { Premium } from '../premium'
import { Membership } from './membership'

export const MembershipAddOn = pgTable(
  'membership_add_on',
  {
    id: uuid().primaryKey().defaultRandom(),
    membershipId: uuid()
      .notNull()
      .references(() => Membership.id, { onDelete: 'cascade' }),
    // `restrict`, not `set null`: the column is NOT NULL, so a `set null` action can only ever raise
    // a not-null violation when the invoice is deleted. Matches `Membership.invoiceId`.
    invoiceId: uuid()
      .notNull()
      .references(() => Invoice.id, { onDelete: 'restrict' }),
    organizationId: text()
      .notNull()
      .references(() => organization.id, { onDelete: 'cascade' }),
    seats: integer().notNull(),
    startDate: timestamp({ mode: 'date', withTimezone: true }).notNull().defaultNow(),
    endDate: timestamp({ mode: 'date', withTimezone: true }).notNull(),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [
    // Index for membership lookups
    index('idx_membership_add_on_membership').on(t.membershipId),
  ],
)

export const membershipAddOnRelations = relations(MembershipAddOn, ({ one, many }) => ({
  membership: one(Membership, {
    fields: [MembershipAddOn.membershipId],
    references: [Membership.id],
  }),

  organization: one(organization, {
    fields: [MembershipAddOn.organizationId],
    references: [organization.id],
  }),

  invoice: one(Invoice, {
    fields: [MembershipAddOn.invoiceId],
    references: [Invoice.id],
  }),

  premiums: many(Premium),
}))

export const InsertMembershipAddOnSchema = createInsertSchema(MembershipAddOn, {
  membershipId: z.uuid(),
  invoiceId: z.uuid(),
  organizationId: z.string(),
  seats: z.number().int().positive(),
  startDate: z.date(),
  endDate: z.date(),
})
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .superRefine((data, ctx) => {
    // Validate that endDate is after startDate
    if (data.endDate <= data.startDate) {
      ctx.addIssue({
        code: 'custom',
        message: 'End date must be after start date',
        path: ['endDate'],
      })
    }
  })

export const UpdateMembershipAddOnSchema = createUpdateSchema(MembershipAddOn, {})
  .omit({
    id: true,
    membershipId: true,
    invoiceId: true,
    organizationId: true,
    seats: true,
    startDate: true,
    endDate: true,
    createdAt: true,
    updatedAt: true,
  })
  .partial()

export const SelectMembershipAddOnSchema = createSelectSchema(MembershipAddOn, {
  id: z.uuid(),
  membershipId: z.uuid(),
  invoiceId: z.uuid(),
  organizationId: z.string(),
  seats: z.number().int(),
  startDate: z.date(),
  endDate: z.date(),
  createdAt: z.date(),
  updatedAt: z.date().nullable(),
})

export type InsertMembershipAddOn = typeof MembershipAddOn.$inferInsert
export type UpdateMembershipAddOn = z.infer<typeof UpdateMembershipAddOnSchema>
export type SelectMembershipAddOn = typeof MembershipAddOn.$inferSelect
